import type { AttributeMap } from 'quill/core';
import type { SetRequired } from 'type-fest';
import type { UploadingCarouselItem } from '../image/types';

const getFileDataURI = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = (error) => {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      reject(error);
    };
    reader.readAsDataURL(file);
  });

// Chunk types. https://www.w3.org/TR/PNG/#5Chunk-layout
const TYPES = {
  END: 1229278788,
  PHYS: 1883789683,
  IHDR: 1229472850,
  IDAT: 1229209940,
};

// The first eight bytes of a PNG datastream.
const signature = [137, 80, 78, 71, 13, 10, 26, 10];

function getRetinaAttributes(file: File) {
  return new Promise<{ width: number; span: 'half' } | null>((resolve) => {
    if (file.type !== 'image/png') {
      resolve(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const width = getRetinaWidthFromBuffer(reader.result as ArrayBuffer);
      resolve(typeof width === 'number' ? { width, span: 'half' } : null);
    };
    reader.onerror = () => resolve(null);

    // pHYs should be defined before IDAT so we only need
    // a small slice (most cases 3000 should be enough).
    // Using a larger value here to make it more reliable.
    reader.readAsArrayBuffer(file.slice(0, 6000, file.type));
  });
}

function toUint32(...bytes: number[]) {
  return new Uint32Array(new Uint8Array(bytes).buffer)[0] as number;
}

class Cursor {
  pos: number = signature.length;
  private nextChunkPos: number | null = null;

  constructor(private buffer: Uint8Array) {}

  next() {
    this.pos = this.nextChunkPos as number;
    this.nextChunkPos = null;
  }

  hasNext() {
    return this.pos < this.buffer.length;
  }

  skipNumber() {
    this.pos += 4;
  }

  readNumber() {
    this.skipNumber();
    // The png format is always big-endian
    return toUint32(
      this.buffer[this.pos - 1] as number,
      this.buffer[this.pos - 2] as number,
      this.buffer[this.pos - 3] as number,
      this.buffer[this.pos - 4] as number,
    );
  }

  readChunk() {
    const length = this.readNumber();
    const type = this.readNumber();
    this.nextChunkPos = this.pos + length + 4;
    return type;
  }
}

function getRetinaWidthFromBuffer(arrayBuffer: ArrayBuffer) {
  const buffer = new Uint8Array(arrayBuffer);

  if (signature.some((n, index) => buffer[index] !== n)) {
    return null;
  }

  const cursor = new Cursor(buffer);

  if (cursor.readChunk() !== TYPES.IHDR) {
    return null;
  }
  const width = cursor.readNumber();
  if (!width) {
    return null;
  }
  // skip height
  cursor.skipNumber();

  while (cursor.hasNext()) {
    cursor.next();

    const type = cursor.readChunk();
    if (type === TYPES.END || type === TYPES.IDAT || !type) {
      break;
    }
    if (type === TYPES.PHYS) {
      const pixelsPerUnitX = cursor.readNumber();
      // skip pixelsPerUnitY
      cursor.skipNumber();

      const unitSpecifier = buffer[cursor.pos];
      if (unitSpecifier) {
        if (pixelsPerUnitX === 5669) {
          return width / 2;
        }
      }
      return null;
    }
  }
  return null;
}

export const getImageUploadsForFiles = async (
  files: File[],
): Promise<SetRequired<UploadingCarouselItem, 'uploadPreviewSource'>[]> => {
  return Promise.all(
    files.map(async (file) => {
      const uploadPreviewSource = await getFileDataURI(file);
      const image = new Image();

      let natural: { width: number; height: number } | null = null;
      natural = await new Promise((resolve) => {
        // We avoid using image.decode() here for two reasons:
        // 1. Performance optimization - we only need dimensions, not fully decoded images
        //    since carousel only shows a few image previews at a time for performance reasons
        // 2. Reliability - decode() has known issues with large images in Chrome
        //    See: https://issues.chromium.org/issues/40792189
        image.onload = () =>
          resolve({ width: image.naturalWidth, height: image.naturalHeight });
        image.onerror = () => resolve(null);

        image.src = uploadPreviewSource;
      });

      return {
        uploadPayload: file,
        uploadPreviewSource,
        ...(natural && natural.width > 0
          ? { natural: { width: natural.width, height: natural.height } }
          : {}),
      };
    }),
  );
};

const getImagePlaceholderInfo = async (
  fileOrUrl: File | string,
): Promise<{ value: UploadingCarouselItem[]; attributes?: AttributeMap }> => {
  if (fileOrUrl instanceof File) {
    const [uploadPreviewSource, attributes] = await Promise.all([
      getFileDataURI(fileOrUrl),
      getRetinaAttributes(fileOrUrl),
    ]);

    return {
      value: [
        {
          uploadPayload: fileOrUrl,
          uploadPreviewSource,
          ...(attributes?.width ? { width: attributes.width } : {}),
        },
      ],
      attributes: attributes?.span ? { span: attributes.span } : {},
    };
  }

  return Promise.resolve({
    value: [{ uploadPreviewSource: fileOrUrl, uploadPayload: fileOrUrl }],
    attributes: {},
  });
};

export default getImagePlaceholderInfo;
